import type {
  ClinicalAnatomicalMarkerType,
  ClinicalGeometryType,
  Id,
} from "../../types";
import type {
  AnatomicalTemplate,
} from "./anatomicalTemplates";
import {
  markerTreatmentAnnotation,
  summarizeMarkerTreatments,
  type TreatmentSummaryItem,
} from "./anatomicalAnnotations";
import {
  geometryPoints,
  isAnatomicalPoint,
  markerColor,
  normalizePoint,
  type AnatomicalPoint,
} from "./anatomicalGeometry";

export interface PremiumCanvasMarker {
  id?: Id;
  marker_key: string;
  marker_type:
    ClinicalAnatomicalMarkerType;
  geometry_type:
    ClinicalGeometryType;
  normalized_geometry:
    Record<string, unknown>;
  planned_treatment?:
    Record<string, unknown>;
  label?: string | null;
}

function TemplateArtwork({
  template,
}: {
  template: AnatomicalTemplate;
}) {
  if (template.artwork) {
    const artwork = template.artwork;

    if (artwork.fullImage) {
      return (
        <image
          href={artwork.src}
          x="0"
          y="0"
          width="300"
          height="500"
          preserveAspectRatio="xMidYMid meet"
          pointerEvents="none"
          aria-hidden="true"
        />
      );
    }

    return (
      <svg
        x="0"
        y="0"
        width="300"
        height="500"
        viewBox={`${artwork.cropX} ${artwork.cropY} ${artwork.cropWidth} ${artwork.cropHeight}`}
        preserveAspectRatio="xMidYMid meet"
        overflow="hidden"
        style={{ overflow: "hidden" }}
        pointerEvents="none"
        aria-hidden="true"
      >
        <image
          href={artwork.src}
          x="0"
          y="0"
          width={artwork.sourceWidth}
          height={artwork.sourceHeight}
          preserveAspectRatio="none"
        />
      </svg>
    );
  }

  const skin = "#eadfd5";
  const line = "#8f7c70";
  const detail = "#b8a69a";

  if (template.region === "FACE") {
    const profile =
      template.view === "LEFT" ||
      template.view === "RIGHT";

    if (profile) {
      const flip =
        template.view === "LEFT"
          ? "translate(300 0) scale(-1 1)"
          : undefined;

      return (
        <g transform={flip}>
          <path
            d="M115 55 C155 28 205 47 220 92 C230 122 217 146 226 165 C232 179 220 187 211 190 C205 220 186 246 157 259 C139 267 121 262 108 249 C82 223 72 178 77 133 C81 96 91 72 115 55 Z"
            fill={skin}
            stroke={line}
            strokeWidth="3"
          />
          <path
            d="M209 112 C220 122 228 134 231 148 L217 153"
            fill="none"
            stroke={line}
            strokeWidth="2.5"
          />
          <path
            d="M201 172 C211 176 216 181 217 187"
            fill="none"
            stroke={line}
            strokeWidth="2"
          />
          <path
            d="M142 258 C140 298 126 332 109 356 M181 250 C184 291 200 326 218 350"
            fill="none"
            stroke={line}
            strokeWidth="3"
          />
          <path
            d="M112 355 C143 342 186 342 218 350"
            fill="none"
            stroke={detail}
            strokeWidth="2"
          />
        </g>
      );
    }

    return (
      <g>
        <path
          d="M150 35 C97 35 69 78 73 139 C77 206 108 259 150 270 C192 259 223 206 227 139 C231 78 203 35 150 35 Z"
          fill={skin}
          stroke={line}
          strokeWidth="3"
        />
        <path
          d="M95 114 C111 103 129 104 141 114 M159 114 C171 104 189 103 205 114"
          fill="none"
          stroke={line}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <ellipse cx="119" cy="128" rx="18" ry="8" fill="none" stroke={detail} strokeWidth="2" />
        <ellipse cx="181" cy="128" rx="18" ry="8" fill="none" stroke={detail} strokeWidth="2" />
        <path
          d="M150 127 C143 157 142 171 150 181 C158 178 162 175 164 171"
          fill="none"
          stroke={detail}
          strokeWidth="2"
        />
        <path
          d="M122 211 C139 225 161 225 178 211 C159 205 141 205 122 211 Z"
          fill="none"
          stroke={line}
          strokeWidth="2.5"
        />
        <path d="M91 169 C104 181 111 188 116 199 M209 169 C196 181 189 188 184 199" fill="none" stroke={detail} strokeWidth="1.7" />
        <path d="M115 267 C114 302 101 327 84 351 M185 267 C186 302 199 327 216 351" fill="none" stroke={line} strokeWidth="3" />
        <path d="M84 351 C124 336 176 336 216 351" fill="none" stroke={detail} strokeWidth="2" />
      </g>
    );
  }

  if (template.region === "SCALP") {
    return (
      <g>
        <ellipse
          cx="150"
          cy="245"
          rx="103"
          ry="173"
          fill={skin}
          stroke={line}
          strokeWidth="3"
        />
        <path d="M150 73 L150 417" stroke={detail} strokeDasharray="6 7" strokeWidth="2" />
        <path d="M57 225 C104 205 196 205 243 225" fill="none" stroke={detail} strokeWidth="2" />
        <path d="M68 160 C113 142 187 142 232 160" fill="none" stroke={detail} strokeWidth="2" />
        <path d="M67 310 C112 329 188 329 233 310" fill="none" stroke={detail} strokeWidth="2" />
        <ellipse cx="150" cy="245" rx="38" ry="58" fill="none" stroke={detail} strokeWidth="2" />
        <text x="150" y="48" textAnchor="middle" fill={line} fontSize="13">Frontal</text>
        <text x="150" y="460" textAnchor="middle" fill={line} fontSize="13">Occipital</text>
      </g>
    );
  }

  const lateral =
    template.view === "LEFT" ||
    template.view === "RIGHT";

  if (lateral) {
    const flip =
      template.view === "LEFT"
        ? "translate(300 0) scale(-1 1)"
        : undefined;

    return (
      <g transform={flip}>
        <circle cx="147" cy="52" r="30" fill={skin} stroke={line} strokeWidth="2.5" />
        <path
          d="M132 84 C111 108 110 156 116 206 L108 303 C106 327 116 329 124 306 L137 225 L142 322 L132 468 C132 484 146 486 151 469 L169 334 L176 468 C179 484 194 482 193 466 L185 312 L183 215 C194 174 191 119 171 88 C158 94 143 93 132 84 Z"
          fill={skin}
          stroke={line}
          strokeWidth="2.5"
        />
        <path d="M145 95 C160 119 166 151 164 188" fill="none" stroke={detail} strokeWidth="2" />
        <path d="M143 224 C160 231 177 231 184 216" fill="none" stroke={detail} strokeWidth="2" />
      </g>
    );
  }

  const isBack =
    template.view === "BACK";

  return (
    <g>
      <circle cx="150" cy="48" r="29" fill={skin} stroke={line} strokeWidth="2.5" />
      <path
        d="M119 82 C97 104 92 148 91 197 L69 313 C66 328 77 334 85 319 L111 215 L115 310 L99 465 C98 481 114 487 120 469 L150 336 L180 469 C186 487 202 481 201 465 L185 310 L189 215 L215 319 C223 334 234 328 231 313 L209 197 C208 148 203 104 181 82 C164 91 136 91 119 82 Z"
        fill={skin}
        stroke={line}
        strokeWidth="2.5"
      />
      <path
        d="M150 82 L150 337"
        stroke={detail}
        strokeDasharray="5 6"
        strokeWidth="1.8"
      />
      {isBack ? (
        <>
          <path d="M119 112 C134 123 166 123 181 112" fill="none" stroke={detail} strokeWidth="2" />
          <path d="M116 292 C129 278 142 278 150 295 C158 278 171 278 184 292" fill="none" stroke={detail} strokeWidth="2" />
          <path d="M105 181 C125 170 175 170 195 181" fill="none" stroke={detail} strokeWidth="1.8" />
        </>
      ) : (
        <>
          <path d="M117 135 C128 121 142 122 150 139 C158 122 172 121 183 135" fill="none" stroke={detail} strokeWidth="2" />
          <circle cx="150" cy="225" r="4" fill={detail} />
          <path d="M116 252 C136 262 164 262 184 252" fill="none" stroke={detail} strokeWidth="1.8" />
          <path d="M150 142 L150 292" fill="none" stroke={detail} strokeWidth="1.5" />
        </>
      )}
    </g>
  );
}

function annotationAnchor(
  marker: PremiumCanvasMarker
): AnatomicalPoint | null {
  const geometry =
    marker.normalized_geometry;

  if (isAnatomicalPoint(geometry)) {
    return geometry;
  }

  const points = geometryPoints(geometry);
  if (points.length === 0) return null;

  return {
    x:
      points.reduce(
        (sum, point) => sum + point.x,
        0
      ) / points.length,
    y:
      points.reduce(
        (sum, point) => sum + point.y,
        0
      ) / points.length,
  };
}

export function PremiumTreatmentSummary({
  markers,
}: {
  markers: PremiumCanvasMarker[];
}) {
  const summary =
    summarizeMarkerTreatments(markers);

  if (summary.length === 0) {
    return null;
  }

  return (
    <div
      className="premium-treatment-summary"
      aria-label="Totales del mapa"
    >
      <strong>Totales del mapa</strong>
      <div className="premium-treatment-summary-items">
        {summary.map(
          (item: TreatmentSummaryItem) => (
            <span
              className="premium-treatment-summary-chip"
              key={item.key}
            >
              {item.product}: {new Intl.NumberFormat(
                "es-AR",
                { maximumFractionDigits: 3 }
              ).format(item.dose)} {item.unit}
              <small>
                {item.markerCount}{" "}
                {item.markerCount === 1
                  ? "marca"
                  : "marcas"}
              </small>
            </span>
          )
        )}
      </div>
    </div>
  );
}

export function PremiumAnatomicalCanvas({
  template,
  markers,
  activeType,
  activePoints,
  onPoint,
  showAnnotations = true,
}: {
  template: AnatomicalTemplate;
  markers: PremiumCanvasMarker[];
  activeType?: ClinicalGeometryType;
  activePoints?: AnatomicalPoint[];
  onPoint?: (point: AnatomicalPoint) => void;
  showAnnotations?: boolean;
}) {
  function canvasPoint(event: any) {
    if (!onPoint) return;

    const bounds =
      event.currentTarget
        .getBoundingClientRect();

    onPoint(
      normalizePoint({
        x:
          (event.clientX - bounds.left) /
          bounds.width,
        y:
          (event.clientY - bounds.top) /
          bounds.height,
      })
    );
  }

  function renderAnnotation(
    marker: PremiumCanvasMarker
  ) {
    if (!showAnnotations) return null;

    const label =
      markerTreatmentAnnotation(marker);
    const anchor =
      annotationAnchor(marker);

    if (!label || !anchor) return null;

    const width = Math.min(
      96,
      Math.max(30, label.length * 6.4 + 14)
    );
    const centerX = Math.max(
      width / 2 + 3,
      Math.min(
        300 - width / 2 - 3,
        anchor.x * 300
      )
    );
    const centerY = Math.max(
      15,
      anchor.y * 500 - 22
    );

    return (
      <g
        className="premium-canvas-annotation"
        transform={
          "translate(" + centerX + " " + centerY + ")"
        }
        pointerEvents="none"
      >
        <rect
          x={-width / 2}
          y="-11"
          width={width}
          height="22"
          rx="11"
          fill="#173247"
          fillOpacity=".94"
          stroke="white"
          strokeWidth="1.5"
        />
        <text
          x="0"
          y="4"
          textAnchor="middle"
          fill="white"
          fontSize="11"
          fontWeight="700"
        >
          {label}
        </text>
      </g>
    );
  }

  function renderGeometry(
    marker: PremiumCanvasMarker
  ) {
    const color =
      markerColor(marker.marker_type);
    const geometry =
      marker.normalized_geometry;

    if (
      marker.geometry_type === "POINT" &&
      isAnatomicalPoint(geometry)
    ) {
      return (
        <g key={marker.marker_key}>
          <circle cx={geometry.x * 300} cy={geometry.y * 500} r="9" fill={color} stroke="white" strokeWidth="3" />
          <circle cx={geometry.x * 300} cy={geometry.y * 500} r="15" fill="none" stroke={color} strokeOpacity=".38" strokeWidth="2" />
          {renderAnnotation(marker)}
        </g>
      );
    }

    const points =
      geometryPoints(geometry);
    const value =
      points.map(
        (point) =>
          `${point.x * 300},${point.y * 500}`
      ).join(" ");

    if (
      marker.geometry_type === "LINE" &&
      points.length >= 2
    ) {
      return (
        <g key={marker.marker_key}>
          <polyline points={value} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          {renderAnnotation(marker)}
        </g>
      );
    }

    if (
      marker.geometry_type === "AREA" &&
      points.length >= 3
    ) {
      return (
        <g key={marker.marker_key}>
          <polygon points={value} fill={color} fillOpacity=".25" stroke={color} strokeWidth="3" />
          {renderAnnotation(marker)}
        </g>
      );
    }

    return null;
  }

  const previewValue =
    (activePoints || []).map(
      (point) =>
        `${point.x * 300},${point.y * 500}`
    ).join(" ");

  return (
    <svg
      viewBox="0 0 300 500"
      role="img"
      aria-label={`Mapa anatómico ${template.label}`}
      onClick={canvasPoint}
      className="premium-anatomical-canvas"
      style={{
        width: "100%",
        maxHeight: 590,
        background:
          "linear-gradient(180deg,#fffdfb 0%,#f4eee8 100%)",
        border:
          "1px solid rgba(80,60,50,.16)",
        borderRadius: 18,
        cursor: onPoint
          ? "crosshair"
          : "default",
        touchAction: "none",
      }}
    >
      <TemplateArtwork template={template} />

      {markers.map(renderGeometry)}

      {(activePoints || []).map(
        (point, index) => (
          <circle
            key={`active-${index}`}
            cx={point.x * 300}
            cy={point.y * 500}
            r="5"
            fill="#173247"
            stroke="white"
            strokeWidth="2"
          />
        )
      )}

      {activeType === "LINE" &&
        (activePoints || []).length >= 2 && (
          <polyline
            points={previewValue}
            fill="none"
            stroke="#173247"
            strokeWidth="4"
            strokeDasharray="7 5"
          />
        )}

      {activeType === "AREA" &&
        (activePoints || []).length >= 3 && (
          <polygon
            points={previewValue}
            fill="#173247"
            fillOpacity=".16"
            stroke="#173247"
            strokeWidth="2"
            strokeDasharray="7 5"
          />
        )}
    </svg>
  );
}
